// pubmedReviewFieldExtractor.ts
import type { PubMedArticle } from '../types'

export interface ReviewFieldExtraction {
    studyDesignSentence: string
    studyDesignTags: string

    primaryOutcomeSentence: string
    secondaryOutcomeSentence: string
    exploratoryOutcomeSentence: string
    outcomeSentences: string

    studyPopulationSentence: string

    sampleSizeSentence: string
    sampleSizeValue: string
    totalSampleSize: string
    groupSampleSizes: string

    inclusionCriteriaSentence: string
    exclusionCriteriaSentence: string
    ageSentence: string

    costAdjustmentSentence: string
    costCurrencyYears: string

    dataSourceSentence: string
    dataSourceTypes: string

    comparatorSentence: string

    followUpSentence: string

    timePeriodSentence: string
    timePeriodStart: string
    timePeriodEnd: string

    studyDurationSentence: string
    studyDurationValue: string

    objectiveSentence: string
    conclusionSentence: string

    geographySentence: string
    settingSentence: string
    realWorldEvidenceSentence: string
    proHrqolSentence: string
    safetySentence: string

    economicOutcomeSentences: string
    costResultSentences: string
    costTypeTags: string
    resourceUseSentences: string
    resourceUseTags: string
    costEffectivenessSentence: string
    icerSentence: string
    icerValue: string
    budgetImpactSentence: string
    budgetImpactValue: string
    pmpmPppmSentence: string
    pmpmPppmValue: string
    inpatientCostSentence: string
    outpatientCostSentence: string
    pharmacyCostSentence: string
}

type PatternGroup = {
    field: keyof ReviewFieldExtraction
    patterns: RegExp[]
}

const EMPTY_EXTRACTION: ReviewFieldExtraction = {
    studyDesignSentence: '',
    studyDesignTags: '',

    primaryOutcomeSentence: '',
    secondaryOutcomeSentence: '',
    exploratoryOutcomeSentence: '',
    outcomeSentences: '',

    studyPopulationSentence: '',

    sampleSizeSentence: '',
    sampleSizeValue: '',
    totalSampleSize: '',
    groupSampleSizes: '',

    inclusionCriteriaSentence: '',
    exclusionCriteriaSentence: '',
    ageSentence: '',

    costAdjustmentSentence: '',
    costCurrencyYears: '',

    dataSourceSentence: '',
    dataSourceTypes: '',

    comparatorSentence: '',

    followUpSentence: '',

    timePeriodSentence: '',
    timePeriodStart: '',
    timePeriodEnd: '',

    studyDurationSentence: '',
    studyDurationValue: '',

    objectiveSentence: '',
    conclusionSentence: '',

    geographySentence: '',
    settingSentence: '',
    realWorldEvidenceSentence: '',
    proHrqolSentence: '',
    safetySentence: '',

    economicOutcomeSentences: '',
    costResultSentences: '',
    costTypeTags: '',
    resourceUseSentences: '',
    resourceUseTags: '',
    costEffectivenessSentence: '',
    icerSentence: '',
    icerValue: '',
    budgetImpactSentence: '',
    budgetImpactValue: '',
    pmpmPppmSentence: '',
    pmpmPppmValue: '',
    inpatientCostSentence: '',
    outpatientCostSentence: '',
    pharmacyCostSentence: '',
}

const STUDY_DESIGN_TAG_PATTERNS: Array<{ tag: string; patterns: RegExp[] }> = [
    { tag: 'retrospective', patterns: [/\bretrospective\b/i, /\bretrospective chart review\b/i] },
    { tag: 'prospective', patterns: [/\bprospective\b/i] },
    { tag: 'cross-sectional', patterns: [/\bcross[- ]sectional\b/i] },
    { tag: 'cohort', patterns: [/\bcohort study\b/i, /\bcohort\b/i] },
    { tag: 'case-control', patterns: [/\bcase[- ]control\b/i] },
    { tag: 'observational', patterns: [/\bobservational study\b/i, /\bobservational\b/i] },
    { tag: 'randomized', patterns: [/\brandomi[sz]ed\b/i, /\brandomi[sz]ed controlled trial\b/i] },
    { tag: 'double-blind', patterns: [/\bdouble[- ]blind\b/i] },
    { tag: 'single-blind', patterns: [/\bsingle[- ]blind\b/i] },
    { tag: 'open-label', patterns: [/\bopen[- ]label\b/i] },
    { tag: 'pragmatic', patterns: [/\bpragmatic trial\b/i, /\bpragmatic study\b/i] },
    { tag: 'registry', patterns: [/\bregistry study\b/i, /\bpatient registry\b/i, /\bregistry\b/i] },
    { tag: 'claims', patterns: [/\bclaims analysis\b/i, /\bclaims database\b/i, /\badministrative claims\b/i] },
    { tag: 'database', patterns: [/\bdatabase study\b/i, /\bnational database\b/i, /\badministrative database\b/i] },
    { tag: 'survey', patterns: [/\bsurvey\b/i, /\bquestionnaire\b/i, /\bcross[- ]sectional survey\b/i] },
    { tag: 'chart review', patterns: [/\bchart review\b/i, /\bmedical record review\b/i] },
    { tag: 'systematic review', patterns: [/\bsystematic review\b/i] },
    { tag: 'meta-analysis', patterns: [/\bmeta[- ]analysis\b/i] },
    { tag: 'network meta-analysis', patterns: [/\bnetwork meta[- ]analysis\b/i] },
    { tag: 'phase 1', patterns: [/\bphase\s*1\b/i, /\bphase\s*i\b/i] },
    { tag: 'phase 2', patterns: [/\bphase\s*2\b/i, /\bphase\s*ii\b/i] },
    { tag: 'phase 3', patterns: [/\bphase\s*3\b/i, /\bphase\s*iii\b/i] },
    { tag: 'phase 4', patterns: [/\bphase\s*4\b/i, /\bphase\s*iv\b/i] },
]

const DATA_SOURCE_TYPE_PATTERNS: Array<{ tag: string; patterns: RegExp[] }> = [
    { tag: 'registry', patterns: [/\bregistry\b/i, /\bpatient registry\b/i, /\bdisease registry\b/i] },
    { tag: 'claims', patterns: [/\bclaims\b/i, /\bclaims database\b/i, /\badministrative claims\b/i] },
    { tag: 'clinical_trial', patterns: [/\bclinical trial\b/i, /\brandomi[sz]ed trial\b/i, /\bphase\s*[1-4ivx]+\b/i] },
    { tag: 'survey', patterns: [/\bsurvey\b/i, /\bquestionnaire\b/i] },
    { tag: 'ehr_emr', patterns: [/\belectronic health record\b/i, /\bEHR\b/, /\bEMR\b/, /\bmedical record\b/i] },
    { tag: 'database', patterns: [/\bdatabase\b/i, /\badministrative database\b/i, /\bnational database\b/i] },
]

const OUTCOME_PATTERNS: RegExp[] = [
    /\boutcome\b/i,
    /\boutcomes\b/i,
    /\bendpoint\b/i,
    /\bendpoints\b/i,
    /\bmeasure\b/i,
    /\bend point\b/i,
    /\bend points\b/i,
    /\beffectiveness\b/i,
    /\befficacy\b/i,
    /\bassess(?:ed|ment)?\b/i,
    /\bevaluat(?:ed|ion)\b/i,
    /\bmeasur(?:ed|ement)\b/i,
    /\bchange in\b/i,
    /\breduction in\b/i,
    /\bimprovement in\b/i,
    /\bresolution\b/i,
    /\brecovery\b/i,
    /\bresponse\b/i,
    /\btime to\b/i,
    /\bincidence of\b/i,
    /\brate of\b/i,
]

const ECONOMIC_OUTCOME_PATTERNS: RegExp[] = [
    /\bcosts?\b/i,
    /\beconomic\b/i,
    /\bbudget impact\b/i,
    /\bcost[- ]effectiveness\b/i,
    /\bcost utility\b/i,
    /\bcost-benefit\b/i,
    /\bresource use\b/i,
    /\bhealthcare resource utilization\b/i,
    /\bHCRU\b/,
    /\bcost savings?\b/i,
    /\bcost offset\b/i,
    /\bmedical costs?\b/i,
    /\bpharmacy costs?\b/i,
    /\btotal healthcare costs?\b/i,
]

const COST_RESULT_PATTERNS: RegExp[] = [
    /\bcosts? (were|was) (lower|higher|reduced|increased)\b/i,
    /\btotal costs? (were|was) (lower|higher)\b/i,
    /\bsignificant(?:ly)? lower costs?\b/i,
    /\bsignificant(?:ly)? higher costs?\b/i,
    /\bcost savings?\b/i,
    /\bcost offset\b/i,
    /\breduction in costs?\b/i,
    /\bincrease in costs?\b/i,
    /\blower healthcare costs?\b/i,
    /\bhigher healthcare costs?\b/i,
]

const RESOURCE_USE_PATTERNS: RegExp[] = [
    /\bhealthcare resource utilization\b/i,
    /\bHCRU\b/,
    /\bhospitali[sz]ations?\b/i,
    /\binpatient stays?\b/i,
    /\boutpatient visits?\b/i,
    /\bemergency department visits?\b/i,
    /\bER visits?\b/i,
    /\bED visits?\b/i,
    /\bpharmacy utilization\b/i,
    /\bresource use\b/i,
]

const COST_TYPE_PATTERNS: Array<{ tag: string; patterns: RegExp[] }> = [
    {
        tag: 'total_healthcare',
        patterns: [/\btotal healthcare costs?\b/i, /\btotal medical costs?\b/i, /\ball-cause costs?\b/i],
    },
    { tag: 'inpatient', patterns: [/\binpatient\b/i, /\bhospitali[sz]ation\b/i, /\bhospital costs?\b/i] },
    { tag: 'outpatient', patterns: [/\boutpatient\b/i, /\bambulatory\b/i, /\bclinic visits?\b/i] },
    { tag: 'er_ed', patterns: [/\bemergency department\b/i, /\bED visits?\b/i, /\bER visits?\b/i] },
    {
        tag: 'pharmacy',
        patterns: [/\bpharmacy costs?\b/i, /\bprescription costs?\b/i, /\bdrug costs?\b/i, /\bmedication costs?\b/i],
    },
    { tag: 'administration', patterns: [/\badministration costs?\b/i, /\binfusion costs?\b/i] },
    {
        tag: 'monitoring_diagnostics',
        patterns: [/\bmonitoring costs?\b/i, /\blaboratory costs?\b/i, /\bdiagnostic costs?\b/i, /\btesting costs?\b/i],
    },
    { tag: 'indirect', patterns: [/\bindirect costs?\b/i, /\bnon-medical costs?\b/i] },
    {
        tag: 'productivity',
        patterns: [/\bproductivity\b/i, /\babsenteeism\b/i, /\bpresenteeism\b/i, /\bwork productivity\b/i],
    },
    { tag: 'caregiver', patterns: [/\bcaregiver burden\b/i, /\bcaregiver time\b/i, /\bcaregiver costs?\b/i] },
    {
        tag: 'budget_impact',
        patterns: [/\bbudget impact\b/i, /\bincremental budget impact\b/i, /\bPMPM\b/, /\bPPPM\b/],
    },
    {
        tag: 'cost_effectiveness',
        patterns: [/\bcost[- ]effectiveness\b/i, /\bICER\b/, /\bcost per QALY\b/i, /\bQALY\b/],
    },
]

const RESOURCE_USE_TAG_PATTERNS: Array<{ tag: string; patterns: RegExp[] }> = [
    {
        tag: 'hospitalization',
        patterns: [/\bhospitali[sz]ations?\b/i, /\binpatient admissions?\b/i, /\binpatient stays?\b/i],
    },
    {
        tag: 'outpatient_visits',
        patterns: [/\boutpatient visits?\b/i, /\bambulatory visits?\b/i, /\bclinic visits?\b/i],
    },
    { tag: 'er_ed_visits', patterns: [/\bemergency department visits?\b/i, /\bED visits?\b/i, /\bER visits?\b/i] },
    { tag: 'pharmacy_use', patterns: [/\bpharmacy utilization\b/i, /\bprescription fills?\b/i, /\bmedication use\b/i] },
    { tag: 'length_of_stay', patterns: [/\blength of stay\b/i, /\bLOS\b/] },
]

const GROUPS: PatternGroup[] = [
    {
        field: 'studyDesignSentence',
        patterns: [
            /\bstudy design\b/i,
            /\bretrospective study\b/i,
            /\bprospective study\b/i,
            /\bcross[- ]sectional\b/i,
            /\bcohort study\b/i,
            /\bcase[- ]control\b/i,
            /\bobservational study\b/i,
            /\brandomi[sz]ed\b/i,
            /\bdouble[- ]blind\b/i,
            /\bsingle[- ]blind\b/i,
            /\bactive[- ]controlled\b/i,
            /\bplacebo[- ]controlled\b/i,
            /\bopen[- ]label\b/i,
            /\bpragmatic trial\b/i,
            /\bregistry study\b/i,
            /\bclaims analysis\b/i,
            /\bchart review\b/i,
            /\bsystematic review\b/i,
            /\bmeta[- ]analysis\b/i,
            /\bphase\s*[1-4ivx]+\b/i,
            /\b(?:non[- ]inferiority|superiority|equivalence) (?:trial|design)\b/i,
            /\bcluster[- ]randomi[sz]ed\b/i,
            /\badaptive (?:design|trial)\b/i,
            /\b(?:two-way )?crossover\b/i,
            /\bmixed[- ]methods\b/i,
            /\bdelphi (?:study|panel|process)\b/i,
            /\bpropensity[- ]score[ -](?:matched|weighting)\b/i,
            /\bsynthetic (?:control|comparator|arm)\b/i,
            /\bdigital twin[s]?\b/i,
        ],
    },
    {
        field: 'primaryOutcomeSentence',
        patterns: [
            /\bcoprimary endpoints?\b/i,
            /\bco-primary endpoints?\b/i,

            /\bprimary outcomes?\b/i,
            /\bprimary endpoints?\b/i,
            /\bprimary end points?\b/i,
            /\bprimary measures?\b/i,

            /\bmain outcomes?\b/i,
            /\bmain endpoints?\b/i,
            /\bmain end points?\b/i,

            /\bprimary objective\b/i,
            /\bthe primary aim\b/i,
        ],
    },
    {
        field: 'secondaryOutcomeSentence',
        patterns: [
            /\bsecondary outcomes?\b/i,
            /\bsecondary endpoints?\b/i,
            /\bsecondary measures?\b/i,
            /\bsecondary end points?\b/i,
            /\bsecondary objectives?\b/i,
        ],
    },
    {
        field: 'exploratoryOutcomeSentence',
        patterns: [
            /\bexploratory outcomes?\b/i,
            /\bexploratory endpoints?\b/i,
            /\bexploratory meaesures?\b/i,
            /\bexploratory end points?\b/i,
            /\bexploratory objective\b/i,
            /\bexploratory analysis\b/i,
            /\bexploratory analyses\b/i,
        ],
    },
    {
        field: 'studyPopulationSentence',
        patterns: [
            /\bstudy population\b/i,
            /\bpatients with\b/i,
            /\bparticipants with\b/i,
            /\bsubjects with\b/i,
            /\badults with\b/i,
            /\bchildren with\b/i,
            /\badolescents with\b/i,
            /\beligible patients\b/i,
            /\bthis study included\b/i,
            /\bthe cohort included\b/i,
            /\bwe enrolled\b/i,
            /\bwe included\b/i,
        ],
    },
    {
        field: 'inclusionCriteriaSentence',
        patterns: [
            /\binclusion criteria\b/i,
            /\beligibility criteria\b/i,
            /\beligible if\b/i,
            /\bpatients were eligible\b/i,
            /\bparticipants were eligible\b/i,
            /\bincluded if\b/i,
            /\bwere included if\b/i,
            /\brequired to have\b/i,
            /\beligibility included\b/i,
        ],
    },
    {
        field: 'exclusionCriteriaSentence',
        patterns: [
            /\bexclusion criteria\b/i,
            /\bexcluded if\b/i,
            /\bwere excluded if\b/i,
            /\bpatients were excluded\b/i,
            /\bparticipants were excluded\b/i,
            /\bexcluded subjects\b/i,
            /\bexcluded patients\b/i,
            /\bexcluded participants\b/i,
            /\bkey exclusion\b/i,
            /\bexclusion included\b/i,
        ],
    },
    {
        field: 'ageSentence',
        patterns: [
            /\bmean age\b/i,
            /\baverage age\b/i,
            /\bmedian age\b/i,
            /\bage range\b/i,
            /\byears of age\b/i,
            /\baged?\s+\d+\b/i,
            /\b\d+\s+years? old\b/i,
            /\badult patients\b/i,
            /\bpediatric patients\b/i,
            /\bpaediatric patients\b/i,
            /\bchildren aged\b/i,
            /\badolescents aged\b/i,
        ],
    },
    {
        field: 'costAdjustmentSentence',
        patterns: [
            /\binflated to\b/i,
            /\ball costs were adjusted\b/i,
            /\bcosts were adjusted\b/i,
            /\bmedical consumer price index\b/i,
            /\bconsumer price index\b/i,
            /\bmCPI\b/i,
            /\b\d{4}\s+USD\b/i,
            /\bUS dollars\b/i,
            /\beuros?\b/i,
            /\badjusted to\b/i,
            /\bconverted to\b/i,
        ],
    },
    {
        field: 'dataSourceSentence',
        patterns: [
            /\bclaims database\b/i,
            /\badministrative claims\b/i,
            /\belectronic health record\b/i,
            /\belectronic medical record\b/i,
            /\bchart review\b/i,
            /\bclinical trial\b/i,
            /\bregistry\b/i,
            /\bsurvey\b/i,
            /\bquestionnaire\b/i,
            /\bclaims\b/i,
            /\bEHR\b/,
            /\bEMR\b/,
            /\bdatabase\b/i,
        ],
    },
    {
        field: 'comparatorSentence',
        patterns: [
            /\bcompared with\b/i,
            /\bcompared to\b/i,
            /\bwas used to compare\b/i,
            /\bversus\b/i,
            /\bvs\.?\b/i,
            /\bcontrol group\b/i,
            /\bplacebo\b/i,
            /\bstandard of care\b/i,
        ],
    },
    {
        field: 'timePeriodSentence',
        patterns: [
            /\bfrom\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\s+(to|through|until|-)\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\b/i,
            /\bbetween\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\s+and\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\b/i,
            /\bduring\s+the\s+period\s+(from\s+)?(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\s+(to|through|until|-)\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4}\b/i,
            /\bstudy period\b/i,
            /\bobservation period\b/i,
            /\bfollow[- ]up period\b/i,
            /\bdata (were )?collected from\b/i,
            /\bclaims data from\b/i,
            /\bbetween \d{4} and \d{4}\b/i,
            /\bfrom \d{4} to \d{4}\b/i,
        ],
    },
    {
        field: 'followUpSentence',
        patterns: [
            /\bmean follow[- ]up\b/i,
            /\bfollow[- ]up\b/i,
            /\bover \d+\s+(days|weeks|months|years)\b/i,
            /\bat week \d+\b/i,
            /\bat \d+\s+(days|weeks|months|years)\b/i,
            /\bduring a \d+[- ]year period\b/i,
            /\bfor \d+\s+(days|weeks|months|years)\b/i,
        ],
    },
    {
        field: 'studyDurationSentence',
        patterns: [
            /\bup to week \d+\b/i,
            /\bweek \d+\b/i,
            /\bfor \d+\s+(days|weeks|months|years)\b/i,
            /\bover \d+\s+(days|weeks|months|years)\b/i,
            /\bduring \d+\s+(days|weeks|months|years)\b/i,
            /\bfollow[- ]up.*?\d+\s+(days|weeks|months|years)\b/i,
            /\btime to recovery\b/i,
            /\bstudy end\b/i,
        ],
    },
    {
        field: 'objectiveSentence',
        patterns: [
            /\bthe goal of this study was to\b/i,
            /\bthe purpose of this study was to\b/i,
            /\bthis research aimed to understand\b/i,
            /\bthis study aimed to\b/i,
            /\bthis research aimed to\b/i,
            /\bthis(?:\s+\w+){0,4}\s+(?:trial|study|analysis)\s+(?:evaluated|researched|studied|assessed)\b/i,
            /\bthis(?:\s+\w+){0,4}\s+analysis aimed to (?:define|evaluate)\b/i,
            /\bthis study described\b/i,
            /\bthis study estimated\b/i,
            /\bthis research estimated\b/i,
            /\bwe conducted(?:\s+\w+){0,6}\s+to understand\b/i,
            /\bwe conducted\b/i,
            /\bwe assessed\b/i,
            /\bwe studied\b/i,
            /\bwe aimed to\b/i,
            /\bwe sought to\b/i,
            /\bwe evaluated\b/i,
            /\bto understand\b/i,
            /\bto determine\b/i,
            /\bwas used to compare\b/i,
            /\bobjective\b/i,
            /\bobjectives\b/i,
            /\baims?\b/i,
            /\bpurpose\b/i,
        ],
    },
    {
        field: 'conclusionSentence',
        patterns: [
            /\btreatment with\b[\s\S]{0,120}\bled to\b[\s\S]{0,80}\b(?:lower|higher|less|greater|more)\b/i,
            /\bthis real[- ]world exploratory study in the US suggests\b/i,
            /\bthis highlights the need to\b/i,
            /\bwas well[- ]tolerated and associated with\b/i,
            /\b(?:patients|subjects)\s+were\s+\d+(?:\.\d+)?\s+times\s+(?:more|less)\s+likely to\b/i,
            /\bin this\b(?:\s+\w+){0,3}\s+(?:analysis|analyses|study)\b[\s\S]{0,80}\bthe majority\b/i,
            /\bmajority of (?:individuals|subjects|patients)\b[\s\S]{0,80}\b(?:indicating|suggesting) that\b/i,

            /\bthis(?:\s+\w+){0,8}\s+study(?:\s+\w+){0,8}\s+(?:showed|demonstrates?|demonstrated|suggests|found|indicates) that\b/i,
            /\bthis(?:\s+\w+){0,8}\s+study(?:\s+\w+){0,8}\s+(?:showed|demonstrates?|demonstrated|suggests|found|indicates)\b/i,

            /\bthese(?:\s+\w+){0,8}\s+findings(?:\s+\w+){0,8}\s+(?:show|demonstrate(?:s|d)?|suggest(?:s|ed)?|indicate(?:s|d)?|found) that\b/i,
            /\bthese(?:\s+\w+){0,8}\s+findings(?:\s+\w+){0,8}\s+(?:show|demonstrate(?:s|d)?|suggest(?:s|ed)?|indicate(?:s|d)?|found)\b/i,

            /\bthese results suggest\b/i,
            /\bresults suggest\b/i,
            /\bour findings suggest\b/i,
            /\bthe study found a higher\b/i,
            /\bthis study concluded\b/i,
            /\bthis study showed\b/i,
            /\bthis study demonstrates\b/i,
            /\bthese findings indicate\b/i,
            /\bsupport the use of\b/i,
            /\bprovides evidence that\b/i,

            /\bconclusion\b/i,
            /\bconclusions\b/i,
            /\bin conclusion\b/i,
        ],
    },

    {
        field: 'geographySentence',
        // Add a purpose statement to better understand what we are trying to identify with these keywords
        patterns: [
            /\bsites?\s+in\s+\d+\s+countries\b/i,
            /\b\d+\s+sites?\s+in\s+\d+\s+countries\b/i,
            /\b(?:across|throughout)\s+\d+\s+countries\b/i,

            /\b(?:conducted|enrolled|recruited|located|sites?)\s+in\s+(?:the\s+)?(?:United States|US|USA|Europe|UK|Japan|Germany|France|Italy|Spain|Canada|China|Norway|Sweden|Finland|Korea|Denmark|Latin America|Asia|Switzerland|Austria|Hong Kong|Netherlands)\b/i, // UPDATED expanded country list

            /\bmultinational\b/i,
            /\binternational\b/i,
            /\bAPAC\b/i,

            /\bU\.S\.\b/,
            /\bUnited States\b/i,
            /\bUSA\b/,
            /\bEurope\b/i,
            /\bUnited Kingdom\b/i,
            /\bUK\b/,
            /\bJapan\b/i,
            /\bGermany\b/i,
            /\bFrance\b/i,
            /\bItaly\b/i,
            /\bSpain\b/i,
            /\bCanada\b/i,
            /\bChina\b/i,
            /\bSweden\b/i,
            /\bNorway\b/i,
            /\bDenmark\b/i,
            /\bFinland\b/i,
            /\bKorea\b/i,
            /\bSwitzerland\b/i,
            /\bAustria\b/i,
            /\bHong Kong\b/i,
            /\bNetherlands\b/i,
        ],
    },
    {
        field: 'settingSentence',
        patterns: [
            /\bsingle[- ]center\b/i,
            /\bsingle[- ]centre\b/i,
            /\bmulticenter\b/i,
            /\bmulti[- ]center\b/i,
            /\bmulti[- ]centre\b/i,
            /\bacademic center\b/i,
            /\bacademic centre\b/i,
            /\bcommunity setting\b/i,
            /\boutpatient\b/i,
            /\bambulatory\b/i,
            /\binpatient\b/i,
            /\bhospital\b/i,
            /\btertiary care\b/i,
        ],
    },
    {
        field: 'realWorldEvidenceSentence',
        patterns: [
            /\breal[- ]world\b/i,
            /\broutine clinical practice\b/i,
            /\bobservational\b/i,
            /\bclaims\b/i,
            /\bregistry\b/i,
        ],
    },
    {
        field: 'proHrqolSentence',
        patterns: [
            /\bhealth[- ]related quality of life\b/i,
            /\bquality of life\b/i,
            /\bHRQoL\b/,
            /\bpatient[- ]reported\b/i,
            /\butility\b/i,
            /\bEQ-5D\b/i,
            /\bSF-36\b/i,
            /\bSF-12\b/i,
            /\bPROMIS\b/i,
            /\bLife Quality Index\b/i,
            /\bPRO\b/,
            /\bvisual analog scale\b/i,
            /\bvisual analogue scale\b/i,
            /\bnumeric rating scale\b/i,
        ],
    },
    {
        field: 'safetySentence',
        patterns: [
            /\badverse event?\b/i,
            /\bserious adverse events?\b/i,
            /\btolerability\b/i,
            /\btreatment[- ]emergent adverse events?\b/i,
            /\bsafety endpoint\b/i,
            /\btolerability\b/i,
        ],
    },
    {
        field: 'economicOutcomeSentences',
        patterns: ECONOMIC_OUTCOME_PATTERNS,
    },
    {
        field: 'costResultSentences',
        patterns: COST_RESULT_PATTERNS,
    },
    {
        field: 'resourceUseSentences',
        patterns: RESOURCE_USE_PATTERNS,
    },
    {
        field: 'costEffectivenessSentence',
        patterns: [
            /\bcost[- ]effectiveness\b/i,
            /\bcost effectiveness analysis\b/i,
            /\bcost-utility\b/i,
            /\bcost utility analysis\b/i,
            /\bincremental cost[- ]effectiveness ratio\b/i,
            /\bICER\b/,
            /\bcost per QALY\b/i,
            /\bcost per life[- ]year\b/i,
            /\bnet monetary benefit\b/i,
            /\bNMB\b/,
        ],
    },
    {
        field: 'icerSentence',
        patterns: [
            /\bincremental cost[- ]effectiveness ratio\b/i,
            /\bICER\b/,
            /\bcost per QALY\b/i,
            /\bcost per life[- ]year\b/i,
            /\bcost per LYG\b/i,
        ],
    },
    {
        field: 'budgetImpactSentence',
        patterns: [
            /\bbudget impact\b/i,
            /\bincremental budget impact\b/i,
            /\bannual budget\b/i,
            /\bhealth plan budget\b/i,
            /\bplan costs?\b/i,
            /\bformulary impact\b/i,
        ],
    },
    {
        field: 'pmpmPppmSentence',
        patterns: [/\bPMPM\b/, /\bper member per month\b/i, /\bPPPM\b/, /\bper patient per month\b/i],
    },
    {
        field: 'inpatientCostSentence',
        patterns: [/\binpatient costs?\b/i, /\bhospitali[sz]ation costs?\b/i, /\bhospital costs?\b/i],
    },
    {
        field: 'outpatientCostSentence',
        patterns: [/\boutpatient costs?\b/i, /\bambulatory costs?\b/i, /\bclinic costs?\b/i],
    },
    {
        field: 'pharmacyCostSentence',
        patterns: [/\bpharmacy costs?\b/i, /\bprescription costs?\b/i, /\bdrug costs?\b/i, /\bmedication costs?\b/i],
    },
]

function normalizeText(input: string): string {
    return input.replace(/\s+/g, ' ').trim()
}

function splitIntoSentences(text: string): string[] {
    const cleaned = normalizeText(text)
    if (!cleaned) return []

    return cleaned
        .split(/(?<=[.!?])\s+(?=[A-Z0-9(])/)
        .map((s) => s.trim())
        .filter(Boolean)
}

function findFirstMatchingSentence(sentences: string[], patterns: RegExp[]): string {
    for (const sentence of sentences) {
        if (patterns.some((pattern) => pattern.test(sentence))) {
            return sentence
        }
    }
    return ''
}

function collectMatchingSentences(sentences: string[], patterns: RegExp[]): string[] {
    return sentences.filter((sentence) => patterns.some((pattern) => pattern.test(sentence)))
}

function collectMatchingTags(text: string): string[] {
    const tags = STUDY_DESIGN_TAG_PATTERNS.filter(({ patterns }) => patterns.some((pattern) => pattern.test(text))).map(
        ({ tag }) => tag
    )

    return Array.from(new Set(tags))
}

function collectDataSourceTypes(text: string): string[] {
    const tags = DATA_SOURCE_TYPE_PATTERNS.filter(({ patterns }) => patterns.some((pattern) => pattern.test(text))).map(
        ({ tag }) => tag
    )

    return Array.from(new Set(tags))
}

function findBestSampleSizeSentence(sentences: string[]): string {
    let bestSentence = ''
    let bestScore = -Infinity

    for (const sentence of sentences) {
        let score = 0

        if (/\bn\s*=\s*\d+\b/i.test(sentence)) score += 6
        if (/\ba total of\s+\d+\s+(patients|participants|subjects)\b/i.test(sentence)) score += 6
        if (/\b\d+\s+(patients|participants|subjects)\s+were assessed\b/i.test(sentence)) score += 5
        if (/\b\d+\s+(patients|participants|subjects)\s+were enrolled\b/i.test(sentence)) score += 5
        if (/\b\d+\s+(patients|participants|subjects)\s+were included\b/i.test(sentence)) score += 5
        if (/\bgroup\s+\d+\b/i.test(sentence)) score += 3
        if (/\bcohort\b/i.test(sentence)) score += 2
        if (/\bcontinued treatment\b/i.test(sentence)) score -= 4

        if (score > bestScore) {
            bestScore = score
            bestSentence = sentence
        }
    }

    return bestScore > 0 ? bestSentence : ''
}

function extractGroupedSampleSizes(sentence: string): {
    values: string[]
    total: string
    groups: string
} {
    if (!sentence) return { values: [], total: '', groups: '' }

    const normalized = sentence.replace(/[–—]/g, '-')

    const reverseMatches = Array.from(
        normalized.matchAll(
            /\b(\d{1,6})\b(?:\s+(patients|participants|subjects))?(?:[^.]{0,40}?)\b(group\s+[A-Za-z0-9]+)\b/gi
        )
    )

    if (reverseMatches.length > 0) {
        const seen = new Set<string>()
        const entries = reverseMatches
            .map((m) => ({
                count: Number(m[1]),
                label: m[3].toLowerCase(),
            }))
            .filter((entry) => {
                const key = `${entry.label}:${entry.count}`
                if (seen.has(key)) return false
                seen.add(key)
                return true
            })

        if (entries.length > 0) {
            const total = entries.reduce((sum, e) => sum + e.count, 0)
            return {
                values: entries.map((e) => String(e.count)),
                total: String(total),
                groups: entries.map((e) => `${e.label}: ${e.count}`).join('; '),
            }
        }
    }

    const nMatch = normalized.match(/\bn\s*=\s*(\d{1,6})\b/i)
    if (nMatch?.[1]) {
        return {
            values: [nMatch[1]],
            total: nMatch[1],
            groups: '',
        }
    }

    const totalOfMatch = normalized.match(/\ba total of\s+(\d{1,6})\b/i)
    if (totalOfMatch?.[1]) {
        return {
            values: [totalOfMatch[1]],
            total: totalOfMatch[1],
            groups: '',
        }
    }

    const allCounts = Array.from(normalized.matchAll(/\b(\d{1,6})\s+(patients|participants|subjects)\b/gi)).map((m) =>
        Number(m[1])
    )

    if (allCounts.length > 0) {
        return {
            values: allCounts.map(String),
            total: allCounts.length === 1 ? String(allCounts[0]) : '',
            groups: '',
        }
    }

    return { values: [], total: '', groups: '' }
}

function extractTimePeriod(sentence: string): { start: string; end: string } {
    if (!sentence) return { start: '', end: '' }

    const monthNames = '(January|February|March|April|May|June|July|August|September|October|November|December)'

    const monthYearMatches = sentence.match(new RegExp(`${monthNames}\\s+\\d{4}`, 'gi'))

    if (monthYearMatches && monthYearMatches.length >= 2) {
        return {
            start: monthYearMatches[0],
            end: monthYearMatches[1],
        }
    }

    const yearRangeMatch = sentence.match(/\b(\d{4})\s+(?:to|through|until|-)\s+(\d{4})\b/i)
    if (yearRangeMatch) {
        return {
            start: yearRangeMatch[1],
            end: yearRangeMatch[2],
        }
    }

    const betweenYearMatch = sentence.match(/\bbetween\s+(\d{4})\s+and\s+(\d{4})\b/i)
    if (betweenYearMatch) {
        return {
            start: betweenYearMatch[1],
            end: betweenYearMatch[2],
        }
    }

    return { start: '', end: '' }
}

function extractCostCurrencyYears(sentence: string): string {
    if (!sentence) return ''

    const matches = Array.from(sentence.matchAll(/\b(?:19|20)\d{2}\s+(USD|EUR|GBP)\b/gi)).map((m) => m[0])

    return Array.from(new Set(matches)).join('; ')
}

function extractStudyDurationValue(sentence: string): string {
    if (!sentence) return ''

    const match =
        sentence.match(/\bup to week \d+\b/i) ||
        sentence.match(/\bweek \d+\b/i) ||
        sentence.match(/\b\d+(?:\.\d+)?\s+(days|weeks|months|years)\b/i)

    return match ? match[0] : ''
}

function extractIcerValue(sentence: string): string {
    if (!sentence) return ''

    const match =
        sentence.match(/\bICER\b[^.]*?(\$?\s?\d[\d,]*(?:\.\d+)?)/i) ||
        sentence.match(/\bcost per QALY\b[^.]*?(\$?\s?\d[\d,]*(?:\.\d+)?)/i) ||
        sentence.match(/\bincremental cost[- ]effectiveness ratio\b[^.]*?(\$?\s?\d[\d,]*(?:\.\d+)?)/i)

    return match?.[1] || ''
}

function extractBudgetImpactValue(sentence: string): string {
    if (!sentence) return ''

    const match = sentence.match(/(\$?\s?\d[\d,]*(?:\.\d+)?(?:\s*(million|billion|m|bn))?)/i)
    return match?.[1] || ''
}

function extractPmpmPppmValue(sentence: string): string {
    if (!sentence) return ''

    const match =
        sentence.match(/\bPMPM\b[^.]*?(\$?\s?\d[\d,]*(?:\.\d+)?)/i) ||
        sentence.match(/\bPPPM\b[^.]*?(\$?\s?\d[\d,]*(?:\.\d+)?)/i) ||
        sentence.match(/\bper member per month\b[^.]*?(\$?\s?\d[\d,]*(?:\.\d+)?)/i) ||
        sentence.match(/\bper patient per month\b[^.]*?(\$?\s?\d[\d,]*(?:\.\d+)?)/i)

    return match?.[1] || ''
}

function uniqueJoined(sentences: string[]): string {
    return Array.from(new Set(sentences)).join(' | ')
}

export function extractReviewFields(article: Pick<PubMedArticle, 'title' | 'abstract'>): ReviewFieldExtraction {
    const titleText = normalizeText(article.title || '')
    const abstractText = normalizeText(article.abstract || '')
    const combinedText = normalizeText([titleText, abstractText].filter(Boolean).join(' '))

    if (!combinedText) return { ...EMPTY_EXTRACTION }

    const sentences = splitIntoSentences(abstractText)
    const extraction: ReviewFieldExtraction = { ...EMPTY_EXTRACTION }

    for (const group of GROUPS) {
        extraction[group.field] = findFirstMatchingSentence(sentences, group.patterns)
    }

    extraction.sampleSizeSentence = findBestSampleSizeSentence(sentences)

    extraction.studyDesignTags = collectMatchingTags(combinedText).join('; ')
    extraction.dataSourceTypes = collectDataSourceTypes(combinedText).join('; ')

    const sampleInfo = extractGroupedSampleSizes(extraction.sampleSizeSentence)
    extraction.sampleSizeValue = sampleInfo.values.join('; ')
    extraction.totalSampleSize = sampleInfo.total
    extraction.groupSampleSizes = sampleInfo.groups

    extraction.costCurrencyYears = extractCostCurrencyYears(extraction.costAdjustmentSentence)

    const timePeriod = extractTimePeriod(extraction.timePeriodSentence)
    extraction.timePeriodStart = timePeriod.start
    extraction.timePeriodEnd = timePeriod.end

    extraction.studyDurationValue = extractStudyDurationValue(extraction.studyDurationSentence)

    extraction.icerValue = extractIcerValue(extraction.icerSentence)
    extraction.budgetImpactValue = extractBudgetImpactValue(extraction.budgetImpactSentence)
    extraction.pmpmPppmValue = extractPmpmPppmValue(extraction.pmpmPppmSentence)

    const outcomeMatches = collectMatchingSentences(sentences, OUTCOME_PATTERNS)
    extraction.outcomeSentences = uniqueJoined(outcomeMatches)

    extraction.economicOutcomeSentences = uniqueJoined(collectMatchingSentences(sentences, ECONOMIC_OUTCOME_PATTERNS))

    extraction.costResultSentences = uniqueJoined(collectMatchingSentences(sentences, COST_RESULT_PATTERNS))

    extraction.resourceUseSentences = uniqueJoined(collectMatchingSentences(sentences, RESOURCE_USE_PATTERNS))

    return extraction
}
